import {
  Box,
  IconButton,
  Table,
  TableBody,
  TableContainer,
  Tooltip,
} from '@mui/material';
import { DragDropContext, DropResult, Droppable } from '@hello-pangea/dnd';
import { IFile } from '../../../../@types/file';
import Iconify from '../../../../components/iconify';
import {
  TableEmptyRows,
  TableHeadCustom,
  TableNoData,
  TablePaginationCustom,
  TableProps,
  TableSelectedAction,
  emptyRows,
} from '../../../../components/table';
import FileTableRow from '../item/FileTableRow';
import { arrayToObject } from '@subflow-frontend/utils/arrayToObject';
import { useCallback } from 'react';
import { useDebugContentOrderFeatureFlag } from '@subflow-frontend/hooks/featureFlag/features/useDebugContentOrderFeatureFlag';
import { useUpdateContentPosition } from '@subflow/data-access';

const TABLE_HEAD = [
  { id: 'name', label: 'Name', align: 'left' },
  { id: 'size', label: 'Size', align: 'left', width: 120 },
  { id: 'type', label: 'Type', align: 'center', width: 120 },
  { id: 'access', label: 'Access', align: 'left', width: 120 },
  { id: 'published', label: 'State', align: 'left', width: 120 },
  { id: 'dateCreated', label: 'Date', align: 'left', width: 160 },
  { id: '' },
];

const TABLE_HEAD_WITH_ORDERING = [
  { id: 'id', label: 'Id', align: 'left' },
  { id: 'name', label: 'Name', align: 'left' },
  { id: 'order', label: 'Order', align: 'left' },
  { id: 'size', label: 'Size', align: 'left', width: 120 },
  { id: 'type', label: 'Type', align: 'center', width: 120 },
  { id: 'access', label: 'Access', align: 'left', width: 120 },
  { id: 'published', label: 'State', align: 'left', width: 120 },
  { id: 'dateCreated', label: 'Date', align: 'left', width: 160 },
  { id: '' },
];

const rowsPerPageOptions = [5, 10, 25, 100];

export type TFileListViewProps<ID extends string | number = number> = {
  loading?: boolean;
  table: TableProps<any, ID>;
  tableData: IFile<ID>[];
  isNotFound: boolean;
  onOpenConfirm?: VoidFunction;
  onDeleteRow?: (id: ID) => Promise<void>;
  onClickRow?: (id: ID, row: IFile<ID>) => void;
  totalCount: number;
  onActionClick?: (id: ID, action: string, row: IFile<ID>) => void;
  disableDetailsPanel?: boolean;
  onDragFileIntoCollection?: (
    source: IFile<ID>,
    target: IFile<ID>,
    sourceIndex: number,
    targetIndex: number
  ) => Promise<void>;
  onUpdateOrder?: (
    { source, sourceIndex }: { source: IFile<ID>; sourceIndex: number },
    {
      fileToPlaceBefore,
      fileIndexToPlaceBefore,
      fileIndexToPlaceAfter,
      fileToPlaceAfter,
    }: {
      fileToPlaceBefore?: IFile<ID>;
      fileIndexToPlaceBefore?: number;
      fileToPlaceAfter?: IFile<ID>;
      fileIndexToPlaceAfter?: number;
    }
  ) => Promise<void>;
  disableFolderSelection?: boolean;
  debugContentOrderEnabled?: boolean;
};

const FileListView = <ID extends string | number = number>({
  loading,
  table,
  tableData,
  isNotFound,
  onDeleteRow,
  onOpenConfirm,
  onClickRow,
  totalCount,
  onActionClick,
  disableDetailsPanel,
  onDragFileIntoCollection,
  disableFolderSelection,
  onUpdateOrder,
  debugContentOrderEnabled,
}: TFileListViewProps<ID>) => {
  const {
    dense,
    page,
    order,
    orderBy,
    rowsPerPage,
    //
    selectType,
    selected,
    onSelectRow,
    onSelectAllRows,
    //
    onSort,
    onChangeDense,
    onChangePage,
    onChangeRowsPerPage,
  } = table;

  const denseHeight = dense ? 52 : 72;
  const isDragAndDropEnabled = !!onDragFileIntoCollection;
  const onDragEnd = useCallback(
    async (result: DropResult) => {
      console.log('onDragEnd result', result);

      if (result.combine && onDragFileIntoCollection) {
        const sourceIndex = result.source.index;
        const targetIndex = Number(result.combine.draggableId);
        const target = tableData[targetIndex];
        const source = tableData[sourceIndex];
        if (target.fileType === 'folder') {
          await onDragFileIntoCollection(
            source,
            target,
            sourceIndex,
            targetIndex
          );
        }
      } else if (onUpdateOrder) {
        const sourceIndex = result.source.index;
        const destinationIndex = result.destination?.index;
        if (
          sourceIndex == undefined ||
          destinationIndex == undefined ||
          sourceIndex === destinationIndex
        )
          return;

        const source = tableData[sourceIndex];
        const target = {
          beforeIndex:
            sourceIndex > destinationIndex ? destinationIndex : undefined,
          afterIndex:
            sourceIndex < destinationIndex ? destinationIndex : undefined,
        };
        await onUpdateOrder(
          {
            source,
            sourceIndex,
          },
          {
            fileIndexToPlaceBefore: target.beforeIndex,
            fileToPlaceBefore:
              target?.beforeIndex >= 0 && tableData[target.beforeIndex],
            fileIndexToPlaceAfter: target.afterIndex,
            fileToPlaceAfter:
              target?.afterIndex >= 0 && tableData[target.afterIndex],
          }
        );
      }
    },
    [onDragFileIntoCollection, onUpdateOrder, tableData]
  );

  return (
    <>
      <DragDropContext onDragEnd={onDragEnd}>
        <Box
          sx={{
            px: 1,
            position: 'relative',
            borderRadius: 1.5,
            bgcolor: 'background.neutral',
          }}
        >
          <TableSelectedAction
            dense={dense}
            numSelected={selected.length}
            rowCount={tableData.length}
            selectType={selectType}
            onSelectAllRows={(checked) =>
              onSelectAllRows(
                checked,
                tableData.map((row) => row.id),
                arrayToObject(tableData)
              )
            }
            action={
              <>
                {onOpenConfirm && (
                  <Tooltip title="Delete">
                    <IconButton color="primary" onClick={onOpenConfirm}>
                      <Iconify icon="eva:trash-2-outline" />
                    </IconButton>
                  </Tooltip>
                )}
              </>
            }
            sx={{
              pl: 1,
              pr: 2,
              top: 8,
              left: 8,
              right: 8,
              width: 'auto',
              borderRadius: 1,
            }}
          />
          <Droppable
            isDropDisabled={!isDragAndDropEnabled}
            droppableId="files"
            isCombineEnabled
            direction="vertical"
          >
            {(provided, snapshot) => (
              <div ref={provided.innerRef} {...provided.droppableProps}>
                <TableContainer>
                  <Table
                    size={dense ? 'small' : 'medium'}
                    sx={{
                      minWidth: 960,
                      borderCollapse: 'separate',
                      borderSpacing: '0 8px',
                      '& .MuiTableCell-head': {
                        boxShadow: 'none !important',
                      },
                    }}
                  >
                    <TableHeadCustom
                      selectType={selectType}
                      order={order}
                      orderBy={orderBy}
                      headLabel={
                        debugContentOrderEnabled
                          ? TABLE_HEAD_WITH_ORDERING
                          : TABLE_HEAD
                      }
                      rowCount={tableData.length}
                      numSelected={selected.length}
                      // onSort={onSort}
                      onSelectAllRows={(checked) =>
                        onSelectAllRows(
                          checked,
                          tableData.map((row) => row.id),
                          arrayToObject(tableData)
                        )
                      }
                      sx={{
                        '& .MuiTableCell-head': {
                          bgcolor: 'transparent',
                        },
                      }}
                    />

                    <TableBody>
                      {tableData.map((row, index) => (
                        <FileTableRow
                          index={index}
                          key={row.id}
                          row={row}
                          isDragAndDropEnabled={isDragAndDropEnabled}
                          selected={selected.includes(row.id)}
                          onSelectRow={() => onSelectRow(row.id, row)}
                          onDeleteRow={
                            onDeleteRow
                              ? async () => await onDeleteRow(row.id)
                              : undefined
                          }
                          onActionClick={
                            onActionClick
                              ? (action) => onActionClick(row.id, action, row)
                              : undefined
                          }
                          onClickRow={() => {
                            if (onClickRow) {
                              onClickRow(row.id, row);
                            }
                          }}
                          disableDetailsPanel={disableDetailsPanel}
                          disableFolderSelection={disableFolderSelection}
                          debugContentOrderEnabled={debugContentOrderEnabled}
                        />
                      ))}
                      <TableEmptyRows
                        height={denseHeight}
                        emptyRows={
                          loading
                            ? rowsPerPage
                            : emptyRows(page, rowsPerPage, tableData.length)
                        }
                      />
                      <TableNoData isNotFound={isNotFound} />
                      {provided.placeholder}
                    </TableBody>
                  </Table>
                </TableContainer>
              </div>
            )}
          </Droppable>
        </Box>

        <TablePaginationCustom
          rowsPerPageOptions={rowsPerPageOptions}
          count={totalCount}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={onChangePage}
          onRowsPerPageChange={onChangeRowsPerPage}
          //
          dense={dense}
          onChangeDense={onChangeDense}
          sx={{
            '& .MuiTablePagination-root': { borderTop: 'none' },
            '& .MuiFormControlLabel-root': { px: 0 },
          }}
        />
      </DragDropContext>
    </>
  );
};
export default FileListView;
